<template>
  <DashboardLayout>
    <Breadcrumb :model="items" />
    <div class="px-4 calendar-container">
      <FullCalendar :options="calendarOptions" />
    </div>
  </DashboardLayout>
</template>

<script setup>
import Breadcrumb from 'primevue/breadcrumb';
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid'
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { getSchedule } from "@/api/student";
import { onMounted, ref } from 'vue';

const items = [
  { label: 'Dashboard' },
  { label: 'Class Schedules' },
]

const calendarOptions = ref({
  plugins: [timeGridPlugin],
  initialView: window.innerWidth < 1024 ? 'timeGridDay' : 'timeGridWeek',
  headerToolbar: {
    left: 'title',
    center: '',
    right: 'prev,today,next',
  },
  titleFormat: {
    year: 'numeric',
    month: 'long'
  },
  height: 'auto',
  contentHeight: 'auto',
  aspectRatio: 1.5,
  handleWindowResize: true,
  windowResizeDelay: 100,
  windowResize: (arg) => {
    const calendar = arg.view.calendar;
    if (window.innerWidth < 1024 && arg.view.type !== 'timeGridDay') {
      calendar.changeView('timeGridDay');
    } else if (window.innerWidth >= 1024 && arg.view.type !== 'timeGridWeek') {
      calendar.changeView('timeGridWeek');
    }
  },
  expandRows: true,
  slotMinTime: '08:00:00',
  slotMaxTime: '21:00:00',
  nowIndicator: true,
  allDaySlot: false,
  slotDuration: '01:00:00',
  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
  events: []
})

onMounted(() => {
  loadSchedules();
})

async function loadSchedules() {
  try {
    const response = await getSchedule();
    const classes = response.data.data;

    if(!classes || classes.length === 0){
      return [];
    }
    const now = new Date();

    // Find first future class (next upcoming)
    const futureClasses = classes.filter(c => new Date(c.date) > now);
    let nextUpcomingId = null;
    if (futureClasses.length > 0) {
      nextUpcomingId = futureClasses[0]._id;
    }

    // Map backend classes into FullCalendar events
    const events = classes.map(c => {
      const classStart = new Date(c.date);
      const [hours, minutes] = c.time.split(":").map(Number);
      classStart.setHours(hours, minutes, 0);

      const classEnd = new Date(classStart);
      classEnd.setHours(classEnd.getHours() + 1); // assume 1 hour, or store duration if available

      let colors = {};
      if (classEnd < now) {
        // Past classes → gray
        colors = {
          backgroundColor: '#E0E0E0',
          borderColor: '#9E9E9E',
          textColor: '#424242'
        };
      } else if (c._id === nextUpcomingId) {
        // Next upcoming → green
        colors = {
          backgroundColor: '#C8E6C9',
          borderColor: '#388E3C',
          textColor: '#1B5E20'
        };
      } else {
        // Other upcoming → blue
        colors = {
          backgroundColor: '#BBDEFB',
          borderColor: '#1976D2',
          textColor: '#0D47A1'
        };
      }

      return {
        id: c._id,
        title: `${c.subject} (${c.tutor})`,
        start: classStart,
        end: classEnd,
        ...colors
      };
    });

    calendarOptions.value.events = events;
  } catch (error) {
    console.error("Error loading schedules:", error);
  }
}
</script>


<style>
.calendar-container {
  width: 100%;
}

.fc-col-header-cell-cushion {
  font-weight: 400;
  text-align: center;
  padding: 6px 0;
  font-size: 14px;
  font-family: Poppins;
}

.fc-toolbar-title {
  font-weight: 400;
  font-size: 17px !important;
  font-family: Poppins;
}

.fc-button-primary {
  background-color: transparent !important;
  border-color: transparent !important;
  color: var(--gray-600) !important;
  font-size: 17px !important;
}

.fc-timeGridWeek-view .fc-col-header-cell.fc-day-today {
  background-color: var(--teal-50);
}

.fc-timeGridWeek-view .fc-timegrid-col.fc-day-today {
  background-color: var(--teal-50);
}

@media (max-width: 767px) {
  .breadcrumb {
    font-size: 12px;
  }
}
</style>
